import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldQuestion } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export default function WithdrawalPage() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", booking_code: (params.get("booking") || "").toUpperCase(), reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/legal/withdrawal", f);
      setDone(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (done) return (
    <div className="w-container py-12 max-w-2xl" data-testid="withdrawal-confirmation">
      <div className="w-card p-8 space-y-4">
        <p className="flex items-center gap-2 text-mint-ink font-extrabold text-xl"><CheckCircle2 size={24} aria-hidden="true" /> Widerruf eingegangen</p>
        <p>Wir haben deine Widerrufserklärung elektronisch erhalten und eine Eingangsbestätigung an deine E-Mail-Adresse gesendet.</p>
        <div className="w-card bg-mint-soft border-mint p-4">
          <p className="w-label">Vorgangsnummer</p>
          <p className="text-2xl font-extrabold tracking-wide" data-testid="withdrawal-case-number">{done.case_number}</p>
          <p className="w-muted text-sm mt-2">Eingegangen am {fmtDateTime(done.received_at)}</p>
          {done.status === "needs_review" && <p className="w-muted text-sm mt-1">Dein Vertrag konnte nicht eindeutig zugeordnet werden – wir prüfen deinen Vorgang manuell. Deine Erklärung bleibt mit dem Eingangszeitpunkt gespeichert.</p>}
        </div>
        <p className="w-muted text-sm">Diese Bestätigung dokumentiert nur den Eingang. Die rechtliche Prüfung und eine etwaige Rückabwicklung erfolgen gesondert. Bitte bewahre die Vorgangsnummer auf.</p>
        <Link to="/" className="w-btn w-btn-secondary inline-flex" data-testid="withdrawal-home-link">Zur Startseite</Link>
      </div>
    </div>
  );

  return (
    <div className="w-container py-10 max-w-2xl" data-testid="withdrawal-page">
      <h1 className="text-3xl md:text-4xl font-extrabold mb-2">Deinen Vertrag widerrufen</h1>
      <p className="w-muted mb-6">Hier kannst du deinen gesetzlichen Widerruf elektronisch erklären, sofern für deinen Vertrag ein Widerrufsrecht besteht. Ein Grund ist nicht erforderlich.</p>
      <div className="w-card p-4 mb-6 bg-mint-soft border-mint flex items-start gap-3">
        <ShieldQuestion size={20} className="text-mint-ink shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm">Die Wäscheleistung kommt zwischen dir und dem jeweiligen <strong>Waschhelden</strong> zustande. Washly tritt als <strong>Vermittler</strong> auf und nimmt deine Widerrufserklärung elektronisch entgegen.</p>
      </div>
      <form className="w-card p-6 space-y-4" onSubmit={submit}>
        <label className="block"><span className="w-label">Name</span>
          <input className="w-input" required minLength={2} value={f.name} onChange={set("name")} autoComplete="name" data-testid="withdrawal-name" /></label>
        <label className="block"><span className="w-label">E-Mail für die Bestätigung</span>
          <input className="w-input" type="email" required value={f.email} onChange={set("email")} autoComplete="email" data-testid="withdrawal-email" /></label>
        <label className="block"><span className="w-label">Buchungsnummer (z. B. WL-ABC123)</span>
          <input className="w-input" value={f.booking_code} onChange={set("booking_code")} placeholder="Falls bekannt" data-testid="withdrawal-booking" /></label>
        <label className="block"><span className="w-label">Grund (optional)</span>
          <textarea className="w-input !h-24 py-3" value={f.reason} onChange={set("reason")} data-testid="withdrawal-reason" /></label>
        <button className="w-btn w-btn-primary w-full" disabled={busy} data-testid="withdrawal-submit">{busy ? "Sende…" : "Widerruf bestätigen"}</button>
      </form>
    </div>
  );
}
